import type { DataFieldDef, DataFieldType } from './dataGraph.js';

export function dataFieldPathKey(path: readonly string[]): string {
  return JSON.stringify(path);
}

export function dataFieldPathLabel(path: readonly string[]): string {
  return path.join(' › ');
}

export function resolveDataField(record: unknown, path: readonly string[]): unknown {
  let value = record;
  for (const segment of path) {
    if (!value || typeof value !== 'object' || Array.isArray(value) || !(segment in value)) return undefined;
    value = (value as Record<string, unknown>)[segment];
  }
  return value;
}

export function inferDataFieldType(value: unknown): DataFieldType | null {
  if (typeof value === 'boolean') return 'bool';
  if (typeof value === 'number' && Number.isFinite(value)) return 'number';
  if (typeof value === 'string') return 'string';
  if (Array.isArray(value) && value.length >= 2 && value.length <= 4 && value.every(item => typeof item === 'number' && Number.isFinite(item))) {
    return `vec${value.length}` as DataFieldType;
  }
  return null;
}

export function discoverDataFields(records: unknown, maxDepth = 8): DataFieldDef[] {
  const found = new Map<string, DataFieldDef>();
  const visit = (value: unknown, path: string[], depth: number): void => {
    const type = inferDataFieldType(value);
    if (type) {
      if (path.length) found.set(dataFieldPathKey(path), { path, type });
      return;
    }
    if (!value || typeof value !== 'object' || Array.isArray(value) || depth >= maxDepth) return;
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) visit(child, [...path, key], depth + 1);
  };
  if (records && typeof records === 'object') {
    for (const record of Object.values(records as Record<string, unknown> | unknown[])) visit(record, [], 0);
  }
  return [...found.values()].sort((a, b) => dataFieldPathLabel(a.path).localeCompare(dataFieldPathLabel(b.path)));
}

export function uniqueDataFieldId(path: readonly string[], fields: Readonly<Record<string, DataFieldDef>>): string {
  const leaf = path.at(-1) || 'field';
  if (!Object.hasOwn(fields, leaf)) return leaf;
  const base = path.join('_') || leaf;
  if (!Object.hasOwn(fields, base)) return base;
  let suffix = 2;
  while (Object.hasOwn(fields, `${base}_${suffix}`)) suffix++;
  return `${base}_${suffix}`;
}
